import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useBpmByNdcAndJobTypeTable } from './useBpmByNdcAndJobTypeTable';
import classes from './bpmByNdcAndJobTypeTable.module.scss';
import actionIcon from '../../../../assets/icons/PPSIcons/actionIcon.svg';
import editIcon from '../../../../assets/icons/PPSIcons/editIcon.svg';
import deleteIcon from '../../../../assets/icons/PPSIcons/deleteIcon.svg';
import AddRowModalBpmByNdcAndJobType from './utils/AddRowModalBpmByNdcAndJobType';
import { BpmByNDCAndJobType } from '../../../../Interfaces/ReferenceInterfaces/StandardAndRecipeBpmInterface';
import { InputText } from 'primereact/inputtext';
import searchIcon from '../../../../assets/icons/PPSIcons/searchIcon.svg';
import { useRef } from 'react';
import DeleteConfirmationModalBpmByNdcAndJobType from './utils/DeleteConfirmationModalBpmByNdcAndJobType';

interface BpmByNdcAndJobTypeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const BpmByNdcAndJobTypeTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: BpmByNdcAndJobTypeTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      bpmByNdcAndJobTypeData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      roomOptions,
      onPageChange,
      handleSearchChange,
      searchQuery,
      jobTypeOptions,
      handleExportToExcel,
      fgNDC
    } = useBpmByNdcAndJobTypeTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: BpmByNDCAndJobType) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const roomTypeTemplate = (rowData: BpmByNDCAndJobType) => {
      return `${rowData.room_type.description}`;
    };

    const jobTypeTemplate = (rowData: BpmByNDCAndJobType) => {
      return `${rowData.job_type.description}`;
    };

    if (isLoading && !bpmByNdcAndJobTypeData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    // Calculate pagination info for the data
    const currentPageNum = Math.floor(pagination.first / pagination.rows) + 1;
    const firstRecord = (currentPageNum - 1) * pagination.rows + 1;
    const lastRecord = Math.min(firstRecord + pagination.rows - 1, pagination.totalItems);

    return (
      <div className={classes.bpmByNdcAndJobTypeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>BPM by NDC and Job Type Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                className={classes.searchInput}
                placeholder="Search..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={bpmByNdcAndJobTypeData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? bpmByNdcAndJobTypeData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`Showing ${firstRecord} to ${lastRecord} of ${pagination.totalItems} entries`}
        >
          <Column field="ndc" header="NDC" />
          <Column field="description" header="Description" />
          <Column field="job_type" header="Job Type" body={jobTypeTemplate} />
          <Column field="room_type" header="Room Type" body={roomTypeTemplate} />
          <Column field="bpm" header="BPM" />
          <Column field="mo_hc" header="MO HC" />
          <Column field="pa_hc" header="PA HC" />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalBpmByNdcAndJobType
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          roomOptions={roomOptions}
          jobTypeOptions={jobTypeOptions}
          fgNDC={fgNDC}
        />

        <DeleteConfirmationModalBpmByNdcAndJobType
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
          itemName={selectedRow ? `${selectedRow.ndc} - ${selectedRow.description}` : ''}
        />
      </div>
    );
  }
);

export default BpmByNdcAndJobTypeTable;
